import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { DialogUI } from '../../Interface';
import productionService from '../../../service/production.service';

const hiddenAuditFields = new Set(['updatedAt', 'updatedBy', 'createdAt', 'createdBy', 'syncKey']);
const hiddenSummaryFields = new Set([
  'id',
  'productionId',
  'productionOperationId',
  'productionItemId',
  'lineNum',
  'visOrder',
]);

const sectionConfig = [
  { key: 'operations', label: 'Műveletek' },
  { key: 'items', label: 'Cikkek' },
  { key: 'attachments', label: 'Csatolmányok' },
];

const normalizeHistoryResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const isDateValue = (value) => {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T|\s|$)/.test(value);
};

const areValuesEqual = (left, right) => {
  return String(left ?? '') === String(right ?? '');
};

const formatPrimitiveValue = (value) => {
  if (value == null || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'Igen' : 'Nem';
  }

  if (isDateValue(value)) {
    return dayjs(value).format('YYYY.MM.DD HH:mm');
  }

  return String(value);
};

const formatFieldSide = (side) => {
  if (!side) {
    return '-';
  }

  return formatPrimitiveValue(side.label ?? side.value);
};

const getFieldKey = (field) => {
  return field?.fieldKey ?? field?.fieldName ?? '';
};

const getFieldLabel = (field) => {
  return field?.fieldName ?? field?.fieldKey ?? '';
};

const getHistoryUser = (entry) => {
  return entry?.changedByUserName ?? entry?.user?.userName ?? entry?.userName ?? entry?.changedBy ?? '-';
};

const getHistoryDate = (entry) => {
  return entry?.changedAt ? dayjs(entry.changedAt).format('YYYY.MM.DD HH:mm') : '-';
};

const getEntryFields = (entry) => {
  return Array.isArray(entry?.fields) ? entry.fields : [];
};

const getFieldByKey = (fields, fieldKey) => {
  return fields.find((field) => getFieldKey(field) === fieldKey);
};

const isDeletionEntry = (entry) => {
  const deletedAtField = getFieldByKey(getEntryFields(entry), 'deletedAt');
  return Boolean(deletedAtField?.to?.value && !deletedAtField?.from?.value);
};

const getVisibleFieldChanges = (fields = []) => {
  return fields.filter((field) => {
    const fieldKey = getFieldKey(field);

    if (!fieldKey) {
      return false;
    }

    if (hiddenAuditFields.has(fieldKey)) {
      return false;
    }

    if (fieldKey === 'deletedAt' || fieldKey === 'deletedBy') {
      return false;
    }

    return !areValuesEqual(field?.from?.value, field?.to?.value);
  });
};

const getVisibleRootChanges = (rootChangedFields = []) => {
  return getVisibleFieldChanges(rootChangedFields);
};

const getSummaryFields = (entry) => {
  return getEntryFields(entry).filter((field) => {
    const fieldKey = getFieldKey(field);

    if (!fieldKey) {
      return false;
    }

    if (hiddenAuditFields.has(fieldKey) || hiddenSummaryFields.has(fieldKey)) {
      return false;
    }

    if (fieldKey === 'deletedAt' || fieldKey === 'deletedBy') {
      return false;
    }

    return (
      field?.to?.value != null || field?.to?.label != null || field?.from?.value != null || field?.from?.label != null
    );
  });
};

const getValueFromField = (fields, key) => {
  const field = getFieldByKey(fields, key);
  return field?.to?.label ?? field?.to?.value ?? field?.from?.label ?? field?.from?.value ?? null;
};

const getEntryIdentity = (entry, type) => {
  const fields = getEntryFields(entry);

  if (type === 'operations') {
    const visOrder = getValueFromField(fields, 'visOrder');
    const label = getValueFromField(fields, 'label');
    const operation = getValueFromField(fields, 'operationId') ?? getValueFromField(fields, 'operationName');

    if (visOrder != null && label) {
      return `Művelet ${visOrder} - ${label}`;
    }

    return label ?? operation ?? (visOrder != null ? `Művelet ${visOrder}` : `#${entry?.id ?? '-'}`);
  }

  if (type === 'items') {
    const itemCode = getValueFromField(fields, 'itemCode');
    const itemName = getValueFromField(fields, 'itemName');
    const visOrder = getValueFromField(fields, 'visOrder');

    if (itemCode && itemName && itemCode !== itemName) {
      return visOrder != null ? `Cikk ${visOrder} - ${itemCode} (${itemName})` : `${itemCode} (${itemName})`;
    }

    return itemCode ?? itemName ?? (visOrder != null ? `Cikk ${visOrder}` : `#${entry?.id ?? '-'}`);
  }

  if (type === 'attachments') {
    return getValueFromField(fields, 'name') ?? `Csatolmány #${entry?.id ?? '-'}`;
  }

  return `#${entry?.id ?? '-'}`;
};

const getEntrySecondary = (entry, type) => {
  const fields = getEntryFields(entry);
  const fragments = [];

  if (type === 'operations') {
    const resource = getValueFromField(fields, 'resourceOneId');
    const startDate = getValueFromField(fields, 'startDate');

    if (resource != null) {
      fragments.push(`Erőforrás: ${resource}`);
    }

    if (startDate != null) {
      fragments.push(`Kezdés: ${formatPrimitiveValue(startDate)}`);
    }
  }

  if (type === 'items') {
    const qty = getValueFromField(fields, 'quantity');
    const unit = getValueFromField(fields, 'quantityUnitId') ?? getValueFromField(fields, 'quantityUnitName');

    if (qty != null) {
      fragments.push(`Mennyiség: ${qty}${unit ? ` ${unit}` : ''}`);
    }
  }

  if (type === 'attachments') {
    const extension = getValueFromField(fields, 'extension');

    if (extension != null) {
      fragments.push(`Kiterjesztés: ${extension}`);
    }
  }

  return fragments.length ? fragments.join(' | ') : null;
};

const normalizeEntitySection = (section = {}) => {
  const added = (section?.added ?? []).filter((entry) => getSummaryFields(entry).length > 0);
  const removed = [...(section?.removed ?? [])];
  const updated = [];

  (section?.updated ?? []).forEach((entry) => {
    if (isDeletionEntry(entry)) {
      removed.push(entry);
      return;
    }

    if (getVisibleFieldChanges(getEntryFields(entry)).length > 0) {
      updated.push(entry);
    }
  });

  return { added, removed, updated };
};

const hasSectionContent = (section) => {
  return section.added.length > 0 || section.removed.length > 0 || section.updated.length > 0;
};

const ProductionHistoryDialog = ({ open, onClose, productionId }) => {
  const [historyRows, setHistoryRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !productionId) {
      return;
    }

    setIsLoading(true);
    productionService
      .getHistoryById(productionId)
      .then((data) => {
        setHistoryRows(normalizeHistoryResponse(data));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [open, productionId]);

  const renderChangeTable = (changes) => {
    if (!changes.length) {
      return null;
    }

    return (
      <div className="overflow-hidden rounded border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="border-b border-slate-200 px-3 py-2 text-left">Mező</th>
              <th className="border-b border-slate-200 px-3 py-2 text-left">Régi érték</th>
              <th className="border-b border-slate-200 px-3 py-2 text-left">Új érték</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((field) => (
              <tr key={getFieldKey(field)}>
                <td className="border-b border-slate-100 px-3 py-2 font-medium">{getFieldLabel(field)}</td>
                <td className="border-b border-slate-100 px-3 py-2">{formatFieldSide(field.from)}</td>
                <td className="border-b border-slate-100 px-3 py-2">{formatFieldSide(field.to)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSummaryFields = (entry) => {
    const summaryFields = getSummaryFields(entry).slice(0, 6);

    if (!summaryFields.length) {
      return <div className="text-sm text-slate-500">Nincs megjeleníthető részlet.</div>;
    }

    return (
      <div className="grid grid-cols-12 gap-2 text-sm">
        {summaryFields.map((field) => {
          const preferredValue =
            field?.to?.value != null || field?.to?.label != null
              ? formatFieldSide(field.to)
              : formatFieldSide(field.from);

          return (
            <div
              key={`${entry?.id ?? 'entry'}-${getFieldKey(field)}`}
              className="col-span-12 md:col-span-6 rounded bg-slate-50 px-3 py-2"
            >
              <span className="font-medium">{getFieldLabel(field)}:</span> {preferredValue}
            </div>
          );
        })}
      </div>
    );
  };

  const renderEntityList = (entries, type, mode) => {
    if (!entries.length) {
      return null;
    }

    return (
      <div className="space-y-3">
        {entries.map((entry, index) => {
          const visibleChanges = getVisibleFieldChanges(getEntryFields(entry));
          const title = getEntryIdentity(entry, type);
          const secondary = getEntrySecondary(entry, type);

          return (
            <div key={`${type}-${mode}-${entry?.id ?? index}`} className="rounded border border-slate-200 bg-white p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-semibold">{title}</div>
                  {secondary && <div className="text-sm text-slate-500">{secondary}</div>}
                </div>
                <div
                  className={`rounded px-2 py-1 text-xs font-semibold uppercase ${
                    mode === 'added'
                      ? 'bg-green-100 text-green-800'
                      : mode === 'removed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {mode === 'added' ? 'Új' : mode === 'removed' ? 'Törölt' : 'Módosult'}
                </div>
              </div>

              {mode === 'updated' ? renderChangeTable(visibleChanges) : renderSummaryFields(entry)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderEntitySection = (title, type, section) => {
    if (!hasSectionContent(section)) {
      return null;
    }

    return (
      <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-4">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700">{title}</h4>
        {renderEntityList(section.added, type, 'added')}
        {renderEntityList(section.removed, type, 'removed')}
        {renderEntityList(section.updated, type, 'updated')}
      </div>
    );
  };

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      closeBtn
      headerContent="Módosítások áttekintése"
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '1300px',
          },
        },
      }}
    >
      <div className="mt-4 max-h-[750px] overflow-y-auto overflow-x-hidden custom-scrollbar space-y-4">
        {isLoading && <div className="py-10 text-center">Betöltés...</div>}
        {!isLoading && !historyRows.length && <div className="py-10 text-center">Nincs elérhető előzmény.</div>}
        {!isLoading &&
          historyRows.map((row, index) => {
            const rootChanges = getVisibleRootChanges(row?.changes?.rootChangedFields ?? []);
            const sections = sectionConfig
              .map((config) => ({
                ...config,
                normalized: normalizeEntitySection(row?.changes?.[config.key]),
              }))
              .filter((section) => hasSectionContent(section.normalized));

            const hasVisibleContent = rootChanges.length > 0 || sections.length > 0;

            return (
              <div
                key={row?.revId ?? `${index}-${getHistoryDate(row)}`}
                className="rounded border border-slate-300 bg-white"
              >
                <div className="grid grid-cols-12 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Revízió</div>
                    <div>{row?.revId ?? '-'}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Módosította</div>
                    <div>{getHistoryUser(row)}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Dátum</div>
                    <div>{getHistoryDate(row)}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Következő revízió</div>
                    <div>{row?.nextRevId ?? '-'}</div>
                  </div>
                </div>

                <div className="space-y-3 p-4">
                  {!hasVisibleContent && <div className="text-sm text-slate-500">Nincs megjeleníthető változás.</div>}

                  {rootChanges.length > 0 && (
                    <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-4">
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Gyártás</h4>
                      {renderChangeTable(rootChanges)}
                    </div>
                  )}

                  {sections.map((section) => renderEntitySection(section.label, section.key, section.normalized))}
                </div>
              </div>
            );
          })}
      </div>
    </DialogUI>
  );
};

export default ProductionHistoryDialog;
